import { FC, useMemo, ReactElement, useEffect } from 'react'
import { Wrapper as GoogleWrapper, Status } from '@googlemaps/react-wrapper'
import Image from 'next/image'

import NextSteps from './NextSteps'
import GoogleMap from '@/components/GoogleMap'
import { PropertyReport } from '@/types/property'

interface Props {
  property_address: string
  onEditToggle: () => void
  property_report: PropertyReport
  coordinates?: google.maps.GeocoderGeometry['location']
}

const IMG_RESIZE = 0.06

const ValuationDisplay: FC<Props> = props => {
  const { address, value, beds, baths, livingArea } = props.property_report

  useEffect(() => {
    // this component lives in Fantasia
    const footer = document.getElementById('home-valuation-tool-footer')
    if (footer) {
      // address is from zillow api, so xss is not a concern
      footer.innerHTML = `
        <sup>*</sup>
        See more details for ${address}
        <a target="_blank" href="https://www.zillow.com" ref="noopener noreferrer">on Zillow</a>. ©Zillow, Inc., 2006-2023.
        Use is subject to <a target="_blank" href="https://www.zillow.com/z/corp/terms/" ref="noopener noreferrer">Terms of Use</a>.
        <a target="_blank" href="https://www.zillow.com/z/zestimate/" ref="noopener noreferrer">What's a Zestimate?</a>
        `
    }
    return () => {
      if (footer) footer.innerHTML = ''
    }
  }, [address])

  return (
    <>
      <div className="wrapper-narrow my-16 px-4">
        <div className="display-grid grid-cols-1 md:grid-cols-12">
          <h1 className="md:col-span-8">
            <span className="display-block">{address}</span>
          </h1>
          {beds && baths && livingArea ? (
            <p className="text-slate md:col-span-12">
              {beds} bedrooms, {baths} baths, {livingArea?.toLocaleString()}{' '}
              square feet
            </p>
          ) : null}
        </div>
        <div>
          <button
            onClick={props.onEditToggle}
            className="Button Button--neutral px-4"
          >
            <span className="Icon Icon--xs -mt-3 mr-2">
              <svg
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                focusable="false"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.192 0L0 11.192V15.435H4.242L15.435 4.243L11.192 0ZM9.999 4.021L11.192 2.828L12.606 4.243L11.413 5.435L9.999 4.021ZM1.999 12.02L8.586 5.435L9.999 6.849L3.413 13.435H1.999V12.02Z"
                  className="Icon-glyph"
                  fill="currentColor"
                ></path>
                <title>Edit</title>
              </svg>
            </span>
            Edit address
          </button>
        </div>

        <div className="mt-10 py-4 box-shadow-1 md:display-flex">
          <div className="text-center px-8 py-4 md:py-8 md:w-1/2">
            <div>
              <Image
                src={
                  process.env.NEXT_PUBLIC_HOME_VALUATION_API +
                  '/zillow-logo.png'
                }
                width={1911 * IMG_RESIZE}
                height={400 * IMG_RESIZE}
                alt="Zillow Logo"
              />
            </div>
            <div
              className="mt-4 type-heading-six uppercase"
              style={{ color: '#086cfc' }}
            >
              Your Zestimate® home valuation is
            </div>
            <div className="mt-6 Numeral text-4xl">
              <span className="Numeral-accent">$</span>
              {value.toLocaleString()}
            </div>
            <p className="mt-6 mb-0 type-body-three text-slate">
              Estimates are for informational purposes only. Automated values
              may not represent true worth of a home.{' '}
            </p>
          </div>
          <div className="px-8 py-4 md:py-8 md:w-1/2">
            <GoogleWrapper
              apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}
              libraries={['places']}
              render={render}
            >
              <GoogleMap
                address={props.property_report.address}
                coordinates={props.coordinates}
              />
            </GoogleWrapper>
          </div>
        </div>
      </div>
      <NextSteps />
    </>
  )
}

const render = (status: Status): ReactElement => {
  if (status === Status.FAILURE) return null
  // Can put a loading spinner here
  if (status === Status.LOADING) return null
}

export default ValuationDisplay
